import baseA11yService from './a11yService';
import apiService from './api/ApiService';
import utilService from './UtilService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from './a11yIIBScreenReaderService';
import a11yIIBFocusHandoffService from './a11yIIBFocusHandoffService';

const FOCUS_TIMING_MS = 50;
const FORCEFOCUS_MAX_ATTEMPTS = 10;
const FOCUS_RETURN_EVENT = 150;
const PROXY_FOCUS_DELAY_MS = 100;
const EMERGENCY_LISTENER_TIMEOUT_MS = 5000;

// --- STATE VARIABLES ---
let customFocusTrapListener = null;
let icewormEventListener = null;
let trapFocusSets = [];
let isListenerActive = false;
let lastFocusedElement = null;
let cachedMainScreenContent = null;

// Race condition handling
let pendingIcewormRequests = 0;
let waitingForIcewormReturn = false;
let handlingIcewormReturn = false;
let focusGuardCleanup = null;

function isIcewormHandoffEnabled() {
    const isIepContext = typeof utilService?.isIep === 'function'
        ? utilService.isIep()
        : false;

    return (
        isIepContext &&
        a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v4
        ) &&
        a11yIIBFocusHandoffService.isA11yIIBFocusHandoffEnabled()
    );
}

// --- BOOKEND PROXIES ---
// We inject landmarks at BOTH the top and bottom of the DOM.
// - Top Proxy: Catches "Shift+D" from Header.
// - Bottom Proxy: Catches "D" from Main/Nav.
function createIcewormToolbarProxies() {
    const createProxy = (id, direction, insertFn) => {
        let proxy = document.getElementById(id);
        if (!proxy) {
            proxy = document.createElement('div');
            proxy.id = id;
            proxy.setAttribute('role', 'region');
            proxy.setAttribute('aria-label', 'contentinfo');
            proxy.setAttribute('tabindex', '0');

            Object.assign(proxy.style, {
                position: 'absolute',
                left: '0',
                width: '100%',
                height: '1px',
                padding: '0',
                margin: '0',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                border: '0',
                zIndex: '99999',
            });

            if (direction === 'backward') proxy.style.top = '0';
            else proxy.style.bottom = '0';

            proxy.innerHTML = '&nbsp;';

            proxy.addEventListener('focus', () => {
                apiService.sendLastPageTabNavigationSignal(direction)
                    .catch(err => console.error('[Iceworm] Proxy signal failed:', err));
                proxy.blur();
            });
        }
        insertFn(proxy);
    };

    createProxy('iceworm-proxy-top', 'forward', (el) => {
        document.body.insertBefore(el, document.body.firstChild);
    });

    createProxy('iceworm-proxy-bottom', 'forward', el => document.body.appendChild(el));
}

function getOrCreateIcewormFocusParking() {
    let parking = document.getElementById('a11y-focus-parking');
    if (!parking) {
        parking = document.createElement('div');
        parking.id = 'a11y-focus-parking';
        parking.setAttribute('tabindex', '-1');
        parking.setAttribute('role', 'group');
        parking.setAttribute('aria-label', '\u2800'); // Braille blank

        Object.assign(parking.style, {
            position: 'fixed',
            left: '-9999px',
            top: '0',
            width: '1px',
            height: '1px',
            outline: 'none',
        });

        parking.innerHTML = '';
        document.body.appendChild(parking);
    }
    return parking;
}

function isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        element.offsetParent !== null
    );
}

function ensureElementFocusable(element) {
    if (!element) return;
    if (!element.getAttribute('tabindex') || element.getAttribute('tabindex') === '-1') {
        element.setAttribute('tabindex', '0');
    }
}

function forceFocusToElement(targetElement, maxAttempts = FORCEFOCUS_MAX_ATTEMPTS) {
    return new Promise((resolve) => {
        const attempt = (count = 0) => {
            if (count >= maxAttempts) {
                resolve(false);
                return;
            }

            if (targetElement) {
                if (!targetElement.hasAttribute('tabindex')) {
                    ensureElementFocusable(targetElement);
                }
                targetElement.focus();
            }

            if (document.activeElement === targetElement) {
                resolve(true);
                return;
            }
            setTimeout(() => attempt(count + 1), FOCUS_TIMING_MS);
        };
        attempt(0);
    });
}

function createFocusGuard(target, emergencyTabListener) {
    if (!target) return () => {};

    let elapsed = 0;
    let cleared = false;
    let guardId = null;

    const clearGuard = () => {
        if (cleared) return;
        cleared = true;
        if (guardId) clearInterval(guardId);
        document.removeEventListener('keydown', tabListener);
        if (emergencyTabListener) document.removeEventListener('keydown', emergencyTabListener);
        handlingIcewormReturn = false;
        focusGuardCleanup = null;
    };

    const tabListener = (e) => {
        const isTab = e.key === 'Tab' || e.keyCode === 9;
        if (isTab) clearGuard();
    };

    document.addEventListener('keydown', tabListener);

    guardId = setInterval(() => {
        if (document.activeElement !== target) target.focus();
        elapsed += FOCUS_TIMING_MS;
        if (elapsed >= FOCUS_TIMING_MS * FORCEFOCUS_MAX_ATTEMPTS) {
            window.focus();
            target.focus();
            clearGuard();
        }
    }, FOCUS_TIMING_MS);

    return clearGuard;
}

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'li[tabindex]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

function createEmergencyTabListener() {
    let removed = false;
    let timeoutId = null;
    const listener = (e) => {
        const isTab = e.key === 'Tab' || e.keyCode === 9;
        if (isTab && handlingIcewormReturn) {
            handlingIcewormReturn = false;
            document.removeEventListener('keydown', listener);
            removed = true;
            if (timeoutId) clearTimeout(timeoutId);
        }
    };

    document.addEventListener('keydown', listener);

    // FIX 2: Use constant and document rationale
    // Failsafe: If the expected focus transition never completes (e.g. error),
    // remove the listener after 5s to prevent memory leaks or stuck state.
    timeoutId = setTimeout(() => {
        if (!removed) {
            document.removeEventListener('keydown', listener);
            removed = true;
        }
    }, EMERGENCY_LISTENER_TIMEOUT_MS);

    return listener;
}

function getVisibleFocusableElements(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(focusableSelector)).filter(isVisible);
}

function determineTargetElement(focusables, direction) {
    if (!focusables.length) return null;
    return direction === 'backward' ? focusables[focusables.length - 1] : focusables[0];
}

function handleIcewormEvent(event) {
    if (!isIcewormHandoffEnabled()) return;

    const direction = event.detail?.direction || 'forward';
    handleIcewormFocusReturn(direction);
    pendingIcewormRequests = 0;
    waitingForIcewormReturn = false;
}

function ensureIcewormListener() {
    if (!icewormEventListener) {
        icewormEventListener = handleIcewormEvent;
        window.addEventListener('icewormFocusReturn', icewormEventListener);
    }
}

function removeIcewormListener() {
    if (icewormEventListener) {
        window.removeEventListener('icewormFocusReturn', icewormEventListener);
        icewormEventListener = null;
    }
}

function handleIcewormFocusReturn(direction) {
    if (!isIcewormHandoffEnabled()) return;
    handlingIcewormReturn = true;
    if (document.activeElement) document.activeElement.blur();

    let target = null;
    const isLandmarkMode = direction.includes('landmark');

    if (isLandmarkMode) {
        if (direction === 'forward_landmark') {
            target = document.querySelector('header') || document.querySelector('[role="banner"]');
        } else if (direction === 'backward_landmark') {
            target = document.querySelector('nav') || document.querySelector('[role="navigation"]');
            if (!target) {
                target = document.querySelector('main') || document.querySelector('[role="main"]');
            }
        }
        if (target && !isVisible(target)) target = null;
    }

    if (!target) {
        if (direction !== 'backward') {
            const skipLink = document.getElementById('skip-to-nav-link');
            if (skipLink) {
                target = skipLink;
            }
        }

        if (!target) {
            const mainContent = document.getElementById('main-screen-content');
            const focusables = mainContent ? getVisibleFocusableElements(mainContent) : [];
            if (focusables.length > 0) {
                target = determineTargetElement(focusables, direction);
                // Check for specific 'candidate-info' focus if moving forward
                if (direction !== 'backward') {
                    const candidateInfo = document.getElementById('candidate-info');
                    const candidateFocusables = candidateInfo ? getVisibleFocusableElements(candidateInfo) : [];

                    if (candidateFocusables.length > 0) {
                        target = candidateFocusables[0];
                    }
                }
            }
        }
    }

    if (!target) {
        handlingIcewormReturn = false;
        return;
    }

    if (isLandmarkMode) {
        if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
            target.addEventListener('blur', function cleanup() {
                target.removeAttribute('tabindex');
                target.removeEventListener('blur', cleanup);
            }, { once: true });
        }
    } else {
        ensureElementFocusable(target);
    }

    const existingAriaLabel = target.getAttribute('aria-label');
    const existingAriaLabelState = target.getAttribute('data-original-aria-label-state');

    if (!existingAriaLabel && !existingAriaLabelState && !target.getAttribute('aria-labelledby') && !target.getAttribute('role')) {
        const textContent = target.textContent?.trim();
        if (textContent) {
            target.setAttribute('aria-label', textContent);
            target.setAttribute('data-original-aria-label-state', 'none');

            const cleanupAriaLabel = () => {
                if (target.getAttribute('data-original-aria-label-state') === 'none') {
                    target.removeAttribute('aria-label');
                    target.removeAttribute('data-original-aria-label-state');
                }
                target.removeEventListener('blur', cleanupAriaLabel);
            };
            target.addEventListener('blur', cleanupAriaLabel, { once: true });
        }
    }

    target.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    target.focus({ preventScroll: true });

    const emergency = createEmergencyTabListener();

    setTimeout(() => {
        if (document.activeElement !== target) {
            target.focus();
        }
        focusGuardCleanup = createFocusGuard(target, emergency);
    }, FOCUS_RETURN_EVENT);
}

const trapFocusListener = (e) => {
    if (!isIcewormHandoffEnabled()) return;
    const KEYCODE_TAB = 9;
    const isTab = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

    if (!isTab) return;
    if (waitingForIcewormReturn) {
        e.preventDefault();
        return;
    }
    if (handlingIcewormReturn) return;

    if (!cachedMainScreenContent || !cachedMainScreenContent.isConnected) {
        cachedMainScreenContent = document.getElementById('main-screen-content');
    }
    const mainContent = cachedMainScreenContent;

    if (mainContent && e.target && e.target.closest && !e.target.closest('#main-screen-content')) {
        const focusables = getVisibleFocusableElements(mainContent);
        if (focusables.length > 0) {
            const first = focusables[0];
            setTimeout(() => {
                if (!handlingIcewormReturn && !waitingForIcewormReturn) {
                    first.focus();
                }
            }, 0);
            e.preventDefault();
            return;
        }
    }

    trapFocusSets.forEach((node) => {
        if (!node.contains(e.target)) return;

        const focusables = getVisibleFocusableElements(node);
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        function sendIcewormSignal(direction) {
            const parking = getOrCreateIcewormFocusParking();
            parking.focus({ preventScroll: true });

            setTimeout(() => {
                pendingIcewormRequests++;
                waitingForIcewormReturn = true;

                apiService.sendLastPageTabNavigationSignal(direction)
                    .catch((err) => {
                        console.error('[a11y] Failed to send signal:', err);
                    })
                    .finally(() => {
                        pendingIcewormRequests--;
                        if (pendingIcewormRequests <= 0) {
                            pendingIcewormRequests = 0;
                            waitingForIcewormReturn = false;
                        }
                    });
            }, PROXY_FOCUS_DELAY_MS);
        }

        function handleBackward() {
            if (node.id === 'main-screen-content' && isIcewormHandoffEnabled()) {
                sendIcewormSignal('backward');
            } else if (last) {
                last.focus();
            }
            e.preventDefault();
        }

        function handleForward() {
            if (node.id === 'main-screen-content' && isIcewormHandoffEnabled()) {
                sendIcewormSignal('forward');
            } else if (first) {
                first.focus();
            }
            e.preventDefault();
        }

        if (e.shiftKey && e.target === first) {
            handleBackward();
        } else if (!e.shiftKey && e.target === last) {
            handleForward();
        }
    });
};

const a11yServiceV2 = {
    ...baseA11yService,

    isIcewormHandoffEnabled,

    tryFocusWithRetry(element, maxAttempts = 5) {
        return forceFocusToElement(element, Math.max(1, maxAttempts));
    },

    focusElementWithTimeout(elementId, fallbackElementId) {
        setTimeout(() => {
            const el = document.getElementById(elementId);
            if (el) {
                this.tryFocusWithRetry(el);
            } else {
                const fallback = document.getElementById(fallbackElementId || 'optionsMenuButton');
                if (fallback) {
                    this.tryFocusWithRetry(fallback);
                }
            }
        }, FOCUS_TIMING_MS);
    },

    focusElement(elementId) {
        const el = document.getElementById(elementId);
        if (el) {
            this.tryFocusWithRetry(el);
        }
    },

    addFocusTrap(containerElement) {
        if (!isIcewormHandoffEnabled()) return;

        createIcewormToolbarProxies();

        if (!containerElement) return;
        if (trapFocusSets.includes(containerElement)) return;

        const focusables = containerElement.querySelectorAll(focusableSelector);
        for (const el of focusables) ensureElementFocusable(el);

        trapFocusSets.push(containerElement);
        if (!isListenerActive) {
            document.addEventListener('keydown', trapFocusListener);
            isListenerActive = true;
        }
        ensureIcewormListener();
    },
    removeFocusTrap(containerElement) {
        if (!isIcewormHandoffEnabled()) return;
        if (!containerElement) return;
        const i = trapFocusSets.indexOf(containerElement);
        if (i > -1) trapFocusSets.splice(i, 1);

        if (trapFocusSets.length === 0) {
            document.removeEventListener('keydown', trapFocusListener);
            isListenerActive = false;
            removeIcewormListener();
        }
    },
    initFocusTrap() {
        if (!isIcewormHandoffEnabled()) return;
        if (customFocusTrapListener) {
            document.removeEventListener('keydown', customFocusTrapListener);
        }

        if (isIcewormHandoffEnabled()) {
            createIcewormToolbarProxies();
        }

        customFocusTrapListener = (e) => {
            const isTab = e.key === 'Tab' || e.keyCode === 9;
            const isEsc = e.key === 'Escape' || e.keyCode === 27;

            if (!isTab && !isEsc) return;

            if (isEsc) {
                this.jumpToLastFocused();
                return;
            }

            for (let i = 0; i < trapFocusSets.length; i++) {
                if (trapFocusSets[i].contains(e.target)) return;
            }

            lastFocusedElement = e.target;
        };
        document.addEventListener('keydown', customFocusTrapListener);
        ensureIcewormListener();
    },

    destroyFocusTrap() {
        if (!isIcewormHandoffEnabled()) return;
        if (customFocusTrapListener) {
            document.removeEventListener('keydown', customFocusTrapListener);
            customFocusTrapListener = null;
        }
        trapFocusSets = [];
        cachedMainScreenContent = null;

        if (isListenerActive) {
            document.removeEventListener('keydown', trapFocusListener);
            isListenerActive = false;
        }

        removeIcewormListener();
        pendingIcewormRequests = 0;
        waitingForIcewormReturn = false;
        handlingIcewormReturn = false;

        if (focusGuardCleanup) {
            focusGuardCleanup();
            focusGuardCleanup = null;
        }
    },

    jumpToLastFocused() {
        if (!isIcewormHandoffEnabled()) return;
        if (lastFocusedElement && document.body.contains(lastFocusedElement)) {
            this.tryFocusWithRetry(lastFocusedElement);
        } else {
            const fallback = document.body.querySelector(focusableSelector);
            if (fallback) {
                this.tryFocusWithRetry(fallback);
            }
        }
    },
};

// ------------------------------------------------------------------
// EXPOSE GLOBALLY FOR C++
// ------------------------------------------------------------------
// eslint-disable-next-line no-underscore-dangle
window.__iceworm_focus_target = function (direction) {
    if (!isIcewormHandoffEnabled()) return;

    const ALLOWED_DIRECTIONS = ['forward', 'backward', 'forward_landmark', 'backward_landmark'];


    if (!direction || typeof direction !== 'string' || !ALLOWED_DIRECTIONS.includes(direction)) {
        console.warn('[Iceworm] Received invalid direction:', direction);
        return;
    }
    handleIcewormFocusReturn(direction);
};

export default a11yServiceV2;
